import axios from 'axios';
import toast from 'react-hot-toast';
import { API_CONFIG, HTTP_STATUS } from '../config/api';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});



// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle 401 errors for authenticated requests
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      const token = localStorage.getItem('token');
      
      // Only logout if we have a token (meaning user was authenticated)
      if (token) {
        localStorage.removeItem('token');
        
        // Use React Router navigation instead of window.location
        // This will be handled by the AuthContext
        window.dispatchEvent(new CustomEvent('auth:logout', { 
          detail: { reason: 'session_expired' } 
        }));
        
        toast.error('Session expired. Please login again.');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN, { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.AUTH.ME);
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.AUTH.PROFILE, profileData);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.AUTH.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getUsers: async (page = 1, limit = 10, search = '', agentId = '') => {
    const params = { page, limit, search };
    if (agentId) {
      params.agentId = agentId;
    }
    const response = await api.get(API_CONFIG.ENDPOINTS.USERS.LIST, { params });
    return response.data;
  },

  getMyUsers: async (page = 1, limit = 10, search = '') => {
    const params = { page, limit, search };
    const response = await api.get(API_CONFIG.ENDPOINTS.USERS.MY_USERS, { params });
    return response.data;
  },

  getUserById: async (id) => {
    const response = await api.get(API_CONFIG.ENDPOINTS.USERS.DETAIL(id));
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post(API_CONFIG.ENDPOINTS.USERS.CREATE, userData);
    return response.data;
  },

  updateUser: async (id, userData) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.USERS.UPDATE(id), userData);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(API_CONFIG.ENDPOINTS.USERS.DELETE(id));
    return response.data;
  },

  updateUserStatus: async (id, status) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.USERS.STATUS(id), { status });
    return response.data;
  },
};

// Categories API
export const categoriesAPI = {
  getCategories: async (page = 1, limit = 10, search = '') => {
    const response = await api.get(API_CONFIG.ENDPOINTS.CATEGORIES.LIST, {
      params: { page, limit, search, admin: 'true' },
    });
    return response.data;
  },

  getCategoryById: async (id) => {
    const response = await api.get(API_CONFIG.ENDPOINTS.CATEGORIES.DETAIL(id));
    return response.data;
  },

  createCategory: async (categoryData) => {
    const response = await api.post(API_CONFIG.ENDPOINTS.CATEGORIES.CREATE, categoryData);
    return response.data;
  },

  updateCategory: async (id, categoryData) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.CATEGORIES.UPDATE(id), categoryData);
    return response.data;
  },

  deleteCategory: async (id) => {
    const response = await api.delete(API_CONFIG.ENDPOINTS.CATEGORIES.DELETE(id));
    return response.data;
  },

  updateCategoryStatus: async (id, status) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.CATEGORIES.STATUS(id), { status });
    return response.data;
  },
};

// Loan Applications API
export const applicationsAPI = {
  getApplications: async (page = 1, limit = 10, search = '', status = '', agentId = '') => {
    const params = { page, limit, search, status };
    if (agentId) {
      params.agentId = agentId;
    }
    const response = await api.get(API_CONFIG.ENDPOINTS.APPLICATIONS.LIST, { params });
    return response.data;
  },

  getApplicationById: async (id) => {
    const response = await api.get(API_CONFIG.ENDPOINTS.APPLICATIONS.DETAIL(id));
    return response.data;
  },

  createApplication: async (applicationData) => {
    const response = await api.post(API_CONFIG.ENDPOINTS.APPLICATIONS.CREATE, applicationData);
    return response.data;
  },

  updateApplication: async (id, applicationData) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.APPLICATIONS.UPDATE(id), applicationData);
    return response.data;
  },

  deleteApplication: async (id) => {
    const response = await api.delete(API_CONFIG.ENDPOINTS.APPLICATIONS.DELETE(id));
    return response.data;
  },

  updateApplicationStatus: async (id, status, comment = '') => {
    const response = await api.put(API_CONFIG.ENDPOINTS.APPLICATIONS.STATUS(id), { status, comment });
    return response.data;
  },

  getApplicationsByUser: async (userId, page = 1, limit = 10) => {
    const response = await api.get(API_CONFIG.ENDPOINTS.APPLICATIONS.BY_USER(userId), {
      params: { page, limit },
    });
    return response.data;
  },

  getApplicationStats: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.APPLICATIONS.STATS);
    return response.data;
  },
};

// Configuration API
export const configAPI = {
  getConfig: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.CONFIG.GET);
    return response.data;
  },

  updateConfig: async (configData) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.CONFIG.UPDATE, configData);
    return response.data;
  },

  updateSplashConfig: async (splashData) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.CONFIG.SPLASH, splashData);
    return response.data;
  },

  updateLoginConfig: async (loginData) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.CONFIG.LOGIN, loginData);
    return response.data;
  },

  updateContactConfig: async (contactData) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.CONFIG.CONTACT, contactData);
    return response.data;
  },

  updateLegalConfig: async (legalData) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.CONFIG.LEGAL, legalData);
    return response.data;
  },

  updateThemeConfig: async (themeData) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.CONFIG.THEME, themeData);
    return response.data;
  },

  updateSMSConfig: async (smsData) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.CONFIG.SMS, smsData);
    return response.data;
  },

  updateDLTConfig: async (dltData) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.CONFIG.DLT, dltData);
    return response.data;
  },

  getForceUpdate: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.CONFIG.GET);
    return response.data.forceUpdate;
  },
  updateForceUpdate: async (forceUpdateData) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.CONFIG.UPDATE, { forceUpdate: forceUpdateData });
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.DASHBOARD.STATS);
    return response.data;
  },

  getRecentApplications: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.DASHBOARD.RECENT_APPLICATIONS);
    return response.data;
  },

  getChartData: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.DASHBOARD.CHART_DATA);
    return response.data;
  },

  getActivityLog: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.DASHBOARD.ACTIVITY_LOG);
    return response.data;
  },
};

// Notifications API
export const notificationsAPI = {
  sendNotification: async (notificationData) => {
    const response = await api.post('/notifications/send', notificationData);
    return response.data;
  },

  getNotificationHistory: async (page = 1, limit = 20) => {
    const response = await api.get('/notifications/history', {
      params: { page, limit },
    });
    return response.data;
  },

  sendBulkSMS: async (smsData) => {
    const response = await api.post('/notifications/bulk-sms', smsData);
    return response.data;
  },
};

// File Upload API
export const uploadAPI = {
  uploadImage: async (file, type = 'general') => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    const response = await api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadDocument: async (file, type = 'document') => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('type', type);

    const response = await api.post('/upload/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Banners API
export const bannersAPI = {
  getBanners: async (page = 1, limit = 10, search = '') => {
    const response = await api.get(API_CONFIG.ENDPOINTS.BANNERS.LIST, {
      params: { page, limit, search },
    });
    return response.data;
  },

  getBannerById: async (id) => {
    const response = await api.get(API_CONFIG.ENDPOINTS.BANNERS.DETAIL(id));
    return response.data;
  },

  createBanner: async (bannerData) => {
    const response = await api.post(API_CONFIG.ENDPOINTS.BANNERS.CREATE, bannerData);
    return response.data;
  },

  updateBanner: async (id, bannerData) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.BANNERS.UPDATE(id), bannerData);
    return response.data;
  },

  deleteBanner: async (id) => {
    const response = await api.delete(API_CONFIG.ENDPOINTS.BANNERS.DELETE(id));
    return response.data;
  },

  updateBannerStatus: async (id, status) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.BANNERS.STATUS(id), { status });
    return response.data;
  },
};

// States API
export const statesAPI = {
  getStates: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.STATES.LIST);
    return response.data;
  },
  getStateById: async (id) => {
    const response = await api.get(API_CONFIG.ENDPOINTS.STATES.DETAIL(id));
    return response.data;
  },
  createState: async (data) => {
    const response = await api.post(API_CONFIG.ENDPOINTS.STATES.CREATE, data);
    return response.data;
  },
  updateState: async (id, data) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.STATES.UPDATE(id), data);
    return response.data;
  },
  deleteState: async (id) => {
    const response = await api.delete(API_CONFIG.ENDPOINTS.STATES.DELETE(id));
    return response.data;
  },
  toggleStateStatus: async (id) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.STATES.STATUS(id));
    return response.data;
  },
};

// Banks API
export const banksAPI = {
  getBanks: async () => {
    const response = await api.get(API_CONFIG.ENDPOINTS.BANKS.LIST);
    return response.data;
  },
  getBankById: async (id) => {
    const response = await api.get(API_CONFIG.ENDPOINTS.BANKS.DETAIL(id));
    return response.data;
  },
  createBank: async (data) => {
    const response = await api.post(API_CONFIG.ENDPOINTS.BANKS.CREATE, data);
    return response.data;
  },
  updateBank: async (id, data) => {
    const response = await api.put(API_CONFIG.ENDPOINTS.BANKS.UPDATE(id), data);
    return response.data;
  },
  deleteBank: async (id) => {
    const response = await api.delete(API_CONFIG.ENDPOINTS.BANKS.DELETE(id));
    return response.data;
  },
  toggleBankStatus: async (id) => {
    const response = await api.patch(API_CONFIG.ENDPOINTS.BANKS.STATUS(id));
    return response.data;
  },
};

// Export the main api instance for direct use
export default api; 